import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAllConversations,
  updateConversation,
  createConversation,
  deleteConversation,
  updateConversationQA,
  generateConversationAudio
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import PlayAudioButton from './PlayAudioButton';
import GrammarCategoriesField from './GrammarCategoriesField';
import { Check, X, Pencil, Trash2, Plus, ChevronDown, ChevronUp, Volume2 } from 'lucide-react';

const INPUT_STYLE = {
  padding: '8px 12px',
  border: '1.5px solid #e5e7eb',
  borderRadius: '6px',
  fontSize: '13px',
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
};

const BUTTON_BASE = {
  padding: '7px 14px',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  transition: 'all 0.15s ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px'
};

const handleFocus = (e) => {
  e.target.style.borderColor = '#3b82f6';
  e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)';
};

const handleBlur = (e) => {
  e.target.style.borderColor = '#e5e7eb';
  e.target.style.boxShadow = 'none';
};

const handleButtonEnter = (e) => {
  e.currentTarget.style.transform = 'translateY(-1px)';
  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
};

const handleButtonLeave = (e) => {
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.boxShadow = 'none';
};

const ConversationManager = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItemData, setNewItemData] = useState({
    topic: '',
    dialogue_number: 1,
    dialogue: [],
    qa_pairs: []
  });
  const [filterTopic, setFilterTopic] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [generatingAudioId, setGeneratingAudioId] = useState(null);
  const [audioResult, setAudioResult] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetchAllConversations();
      setConversations(response.conversations || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const uniqueTopics = useMemo(() => {
    return [...new Set(conversations.map((item) => item.topic))].filter(Boolean);
  }, [conversations]);

  const filteredConversations = useMemo(() => {
    return conversations.filter((conv) => {
      if (filterTopic && conv.topic !== filterTopic) return false;
      return true;
    });
  }, [conversations, filterTopic]);

  const handleEdit = (conv) => {
    setEditingId(conv.id);
    setEditData({
      topic: conv.topic,
      dialogue_number: conv.dialogue_number || 1,
      dialogue: conv.dialogue || [],
      qa_pairs: conv.qa_pairs
    });
  };

  const handleSave = async (id) => {
    try {
      await updateConversation(id, {
        topic: editData.topic,
        dialogue_number: editData.dialogue_number,
        dialogue: editData.dialogue
      });

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === id
            ? { ...conv, topic: editData.topic, dialogue_number: editData.dialogue_number, dialogue: editData.dialogue }
            : conv
        )
      );
      setEditingId(null);
      setEditData({});
    } catch (err) {
      console.error('Error saving conversation:', err);
      alert('Failed to save: ' + err.message);
    }
  };

  const handleSaveQA = async (qaId, questionData) => {
    try {
      await updateConversationQA(qaId, questionData);
      setConversations((prev) =>
        prev.map((conv) => ({
          ...conv,
          qa_pairs: conv.qa_pairs.map((qa) =>
            qa.id === qaId ? { ...qa, ...questionData } : qa
          )
        }))
      );
    } catch (err) {
      console.error('Error saving Q&A:', err);
      alert('Failed to save Q&A: ' + err.message);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This will also delete all Q&A pairs.')) {
      return;
    }
    try {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((conv) => conv.id !== id));
    } catch (err) {
      console.error('Error deleting conversation:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleStartAddNew = () => {
    setIsAddingNew(true);
    setNewItemData({
      topic: '',
      dialogue_number: 1,
      dialogue: [
        { role: 'Speaker1', text: 'Hello!', gender: 'male' },
        { role: 'Speaker2', text: 'Hi there!', gender: 'female' }
      ],
      qa_pairs: [
        { question: '', answer: '', alternative_answers: [], qa_order: 1 }
      ]
    });
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setNewItemData({ topic: '', dialogue_number: 1, dialogue: [], qa_pairs: [] });
  };

  const handleSaveNew = async () => {
    try {
      if (!newItemData.topic.trim()) {
        alert('Topic is required');
        return;
      }

      const qaPairsForApi = newItemData.qa_pairs.map(qa => ({
        ...qa,
        grammar_categories: Array.isArray(qa.grammar_categories) ? qa.grammar_categories : []
      }));

      const newConv = await createConversation({
        topic: newItemData.topic,
        dialogue_number: Number(newItemData.dialogue_number) || 1,
        dialogue: newItemData.dialogue,
        qa_pairs: qaPairsForApi
      });

      setConversations((prev) => [...prev, newConv]);
      setIsAddingNew(false);
      setNewItemData({ topic: '', dialogue_number: 1, dialogue: [], qa_pairs: [] });
    } catch (err) {
      console.error('Error creating conversation:', err);
      alert('Failed to create: ' + err.message);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleGenerateAudioForOne = async (conv) => {
    try {
      setGeneratingAudioId(conv.id);
      setAudioResult(null);
      const data = await generateConversationAudio(conv.id);
      const success = (data.errors || []).length === 0 && data.generated_count > 0;
      setAudioResult({
        success,
        message: `Konverzace ${conv.id} (${conv.topic}, dialog ${conv.dialogue_number ?? 1}): audio vygenerováno pro ${data.generated_count} z ${data.total_lines} řádků.`,
        errors: data.errors
      });
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, has_audio: success } : c))
      );
    } catch (err) {
      console.error('Error generating audio:', err);
      setAudioResult({
        success: false,
        message: `Nepodařilo se vygenerovat audio pro konverzaci ${conv.id}: ` + err.message
      });
    } finally {
      setGeneratingAudioId(null);
    }
  };

  if (loading && conversations.length === 0) {
    return <Loading />;
  }

  if (error && conversations.length === 0) {
    return <Error message={error} />;
  }

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      {audioResult && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          borderRadius: '6px',
          backgroundColor: audioResult.success ? '#d4edda' : '#f8d7da',
          border: `1px solid ${audioResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          color: audioResult.success ? '#155724' : '#721c24'
        }}>
          <p style={{ margin: 0, fontWeight: '500' }}>{audioResult.message}</p>
          {audioResult.errors && audioResult.errors.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.9rem' }}>
                Chyby ({audioResult.errors.length}):
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85rem' }}>
                {audioResult.errors.slice(0, 5).map((err, index) => (
                  <li key={index}>
                    <strong>Konverzace {err.conversation_id}, řádek {err.line_index}</strong>: {err.error}
                  </li>
                ))}
                {audioResult.errors.length > 5 && (
                  <li>... a {audioResult.errors.length - 5} dalších chyb</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: '#1f2937' }}>Dialogy</h2>

        {!isAddingNew && (
          <button
            onClick={handleStartAddNew}
            style={{
              ...BUTTON_BASE,
              padding: '8px 16px',
              backgroundColor: '#16a34a',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#15803d';
              handleButtonEnter(e);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#16a34a';
              handleButtonLeave(e);
            }}
          >
            <Plus size={14} /> Add New
          </button>
        )}

        {uniqueTopics.length > 0 && (
          <div>
            <label htmlFor="topicFilter" style={{ marginRight: '8px', color: '#6b7280' }}>
              Filter by topic:
            </label>
            <select
              id="topicFilter"
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
              style={{
                ...INPUT_STYLE,
              }}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              <option value="">All</option>
              {uniqueTopics.map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </div>
        )}

        <span style={{ color: '#6b7280', fontSize: '0.9rem', backgroundColor: '#f9fafb', padding: '5px 12px', borderRadius: '20px' }}>
          Total: {filteredConversations.length} conversations
        </span>
      </div>

      {/* Add New Form */}
      {isAddingNew && (
        <div style={{
          marginBottom: '20px',
          padding: '20px',
          backgroundColor: '#f0fdf4',
          border: 'none',
          borderTop: '3px solid #16a34a',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#16a34a' }}>Add New Conversation</h3>

          <div style={{ marginBottom: '15px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>
                Topic <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={newItemData.topic}
                onChange={(e) => setNewItemData({ ...newItemData, topic: e.target.value })}
                style={{
                  ...INPUT_STYLE,
                  width: '100%',
                  maxWidth: '300px',
                  border: '1.5px solid #16a34a',
                  backgroundColor: '#f0fdf4',
                  cursor: 'pointer',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#16a34a';
                  e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#16a34a';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">— vyber téma —</option>
                {uniqueTopics.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div style={{ width: '140px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>
                Dialog # <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={newItemData.dialogue_number}
                onChange={(e) => setNewItemData({ ...newItemData, dialogue_number: Number(e.target.value) })}
                style={{
                  ...INPUT_STYLE,
                  width: '100%',
                  border: '1.5px solid #16a34a',
                  backgroundColor: '#f0fdf4',
                  cursor: 'pointer',
                }}
              >
                {[1, 2, 3].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>
              Dialogue
            </label>
            <DialogueEditor
              dialogue={newItemData.dialogue}
              onChange={(newDialogue) => setNewItemData({ ...newItemData, dialogue: newDialogue })}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>
              Q&A Pairs
            </label>
            <QAEditor
              qaPairs={newItemData.qa_pairs}
              onChange={(newQaPairs) => setNewItemData({ ...newItemData, qa_pairs: newQaPairs })}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleSaveNew}
              style={{
                ...BUTTON_BASE,
                padding: '10px 20px',
                backgroundColor: '#16a34a',
                color: 'white',
                fontWeight: 'bold',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#15803d';
                handleButtonEnter(e);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#16a34a';
                handleButtonLeave(e);
              }}
            >
              <Check size={14} /> Save New Conversation
            </button>
            <button
              onClick={handleCancelAddNew}
              style={{
                ...BUTTON_BASE,
                padding: '10px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4b5563';
                handleButtonEnter(e);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#6b7280';
                handleButtonLeave(e);
              }}
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Conversations List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {filteredConversations.map((conv) => (
            <div
              key={conv.id}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: editingId === conv.id ? 'inset 4px 0 0 #f59e0b' : '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              {/* Header Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ fontWeight: 'bold', color: '#6b7280' }}>ID: {conv.id}</span>
                  {editingId === conv.id ? (
                    <>
                      <select
                        value={editData.topic}
                        onChange={(e) => setEditData({ ...editData, topic: e.target.value })}
                        style={{
                          ...INPUT_STYLE,
                          border: '1.5px solid #f59e0b',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#f59e0b';
                          e.target.style.boxShadow = '0 0 0 3px rgba(245,158,11,0.15)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#f59e0b';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        {uniqueTopics.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <select
                        value={editData.dialogue_number || 1}
                        onChange={(e) => setEditData({ ...editData, dialogue_number: Number(e.target.value) })}
                        title="Dialog #"
                        style={{
                          ...INPUT_STYLE,
                          width: '80px',
                          border: '1.5px solid #f59e0b',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                        }}
                      >
                        {[1, 2, 3].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <>
                      <span style={{
                        padding: '4px 12px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                      }}>
                        {conv.topic}
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                      }}>
                        Dialog {conv.dialogue_number ?? 1}
                      </span>
                    </>
                  )}
                  <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                    {conv.dialogue?.length || 0} lines, {conv.qa_pairs?.length || 0} Q&A
                  </span>
                  <span style={{ fontSize: '1.1rem' }} title={conv.has_audio ? 'Audio vygenerováno' : 'Audio chybí'}>
                    {conv.has_audio ? '\u2705' : '\u274C'}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {editingId === conv.id ? (
                    <>
                      <button
                        onClick={() => handleSave(conv.id)}
                        style={{
                          ...BUTTON_BASE,
                          padding: '6px 16px',
                          backgroundColor: '#16a34a',
                          color: 'white',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#15803d';
                          handleButtonEnter(e);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#16a34a';
                          handleButtonLeave(e);
                        }}
                      >
                        <Check size={14} /> Save
                      </button>
                      <button
                        onClick={handleCancel}
                        style={{
                          ...BUTTON_BASE,
                          padding: '6px 16px',
                          backgroundColor: '#6b7280',
                          color: 'white',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#4b5563';
                          handleButtonEnter(e);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#6b7280';
                          handleButtonLeave(e);
                        }}
                      >
                        <X size={14} /> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleGenerateAudioForOne(conv)}
                        disabled={generatingAudioId !== null}
                        title="Vygenerovat audio pro tento dialog"
                        style={{
                          ...BUTTON_BASE,
                          padding: '6px 16px',
                          backgroundColor: generatingAudioId === conv.id ? '#6c757d' : 'transparent',
                          color: generatingAudioId === conv.id ? 'white' : '#0891b2',
                          border: '1.5px solid #0891b2',
                          cursor: generatingAudioId !== null ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          if (generatingAudioId === null) {
                            e.currentTarget.style.backgroundColor = '#0891b2';
                            e.currentTarget.style.color = 'white';
                            handleButtonEnter(e);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (generatingAudioId === null) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#0891b2';
                            handleButtonLeave(e);
                          }
                        }}
                      >
                        {generatingAudioId === conv.id ? (
                          <>
                            <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'⏳'}</span>
                            Generuji...
                          </>
                        ) : (
                          <><Volume2 size={14} /> Vygeneruj audio</>
                        )}
                      </button>
                      <button
                        onClick={() => toggleExpand(conv.id)}
                        style={{
                          ...BUTTON_BASE,
                          padding: '6px 16px',
                          backgroundColor: '#0891b2',
                          color: 'white',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#0e7490';
                          handleButtonEnter(e);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#0891b2';
                          handleButtonLeave(e);
                        }}
                      >
                        {expandedId === conv.id ? <><ChevronUp size={14} /> Collapse</> : <><ChevronDown size={14} /> Expand</>}
                      </button>
                      <button
                        onClick={() => handleEdit(conv)}
                        style={{
                          ...BUTTON_BASE,
                          padding: '6px 16px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#2563eb';
                          handleButtonEnter(e);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#3b82f6';
                          handleButtonLeave(e);
                        }}
                      >
                        <Pencil size={14} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(conv.id)}
                        style={{
                          ...BUTTON_BASE,
                          padding: '6px 16px',
                          backgroundColor: 'transparent',
                          color: '#dc2626',
                          border: '1.5px solid #dc2626',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#dc2626';
                          e.currentTarget.style.color = 'white';
                          handleButtonEnter(e);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#dc2626';
                          handleButtonLeave(e);
                        }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded Content or Edit Mode */}
              {(expandedId === conv.id || editingId === conv.id) && (
                <div style={{ marginTop: '15px', borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
                  {/* Dialogue Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>Dialogue</h4>
                    {editingId === conv.id ? (
                      <DialogueEditor
                        dialogue={editData.dialogue}
                        onChange={(newDialogue) => setEditData({ ...editData, dialogue: newDialogue })}
                      />
                    ) : (
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '15px',
                        borderRadius: '4px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                      }}>
                        {conv.dialogue?.map((line, idx) => (
                          <div key={idx} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {conv.has_audio && (
                              <PlayAudioButton
                                text={line.text}
                                speaker={line.gender === 'female' ? 'af_heart' : 'am_michael'}
                                title="Přehrát audio repliky"
                              />
                            )}
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '11px',
                              backgroundColor: line.gender === 'female' ? '#ffe6e6' : '#e6f0ff',
                              color: line.gender === 'female' ? '#c13c25' : '#007bff'
                            }}>
                              {line.gender === 'female' ? '♀' : '♂'}
                            </span>
                            <strong style={{ color: line.gender === 'female' ? '#c13c25' : '#007bff' }}>{line.role}:</strong>{' '}
                            <span>{line.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Q&A Section */}
                  <div>
                    <h4 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>Q&A Pairs</h4>
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '4px',
                    }}>
                      {conv.qa_pairs?.map((qa, idx) => (
                        <QAItem
                          key={qa.id}
                          qa={qa}
                          index={idx}
                          onSave={handleSaveQA}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {filteredConversations.length === 0 && !loading && (
            <p style={{ textAlign: 'center', color: '#6b7280', fontStyle: 'italic', padding: '20px' }}>
              No conversations found.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Sub-component for Q&A editing
const QAItem = ({ qa, index, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    question: qa.question,
    answer: qa.answer,
    alternative_answers: qa.alternative_answers || [],
    qa_order: qa.qa_order,
    grammar_categories: qa.grammar_categories || []
  });

  const handleSave = async () => {
    await onSave(qa.id, editValues);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValues({
      question: qa.question,
      answer: qa.answer,
      alternative_answers: qa.alternative_answers || [],
      qa_order: qa.qa_order,
      grammar_categories: qa.grammar_categories || []
    });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div style={{
        marginBottom: '15px',
        padding: '10px',
        backgroundColor: '#f9fafb',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
      }}>
        <div style={{ marginBottom: '8px' }}>
          <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', marginRight: '8px' }}>Order:</label>
          <input
            type="number"
            value={editValues.qa_order}
            onChange={(e) => setEditValues({ ...editValues, qa_order: parseInt(e.target.value) || 1 })}
            style={{ ...INPUT_STYLE, width: '60px' }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <div style={{ marginBottom: '8px' }}>
          <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Question:</label>
          <textarea
            value={editValues.question}
            onChange={(e) => setEditValues({ ...editValues, question: e.target.value })}
            style={{ ...INPUT_STYLE, width: '100%', minHeight: '40px' }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <div style={{ marginBottom: '8px' }}>
          <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Answer:</label>
          <textarea
            value={editValues.answer}
            onChange={(e) => setEditValues({ ...editValues, answer: e.target.value })}
            style={{ ...INPUT_STYLE, width: '100%', minHeight: '40px' }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <div style={{ marginBottom: '8px' }}>
          <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Grammar Categories:</label>
          <GrammarCategoriesField
            value={editValues.grammar_categories}
            onChange={(val) => setEditValues({ ...editValues, grammar_categories: val })}
          />
        </div>
        <div style={{ marginBottom: '8px' }}>
          <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Alternative Answers:</label>
          {(editValues.alternative_answers || []).map((alt, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
              <input
                type="text"
                value={alt}
                onChange={(e) => {
                  const updated = [...editValues.alternative_answers];
                  updated[idx] = e.target.value;
                  setEditValues({ ...editValues, alternative_answers: updated });
                }}
                style={{ ...INPUT_STYLE, flex: 1 }}
                placeholder="Alternative answer..."
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
              <button
                onClick={() => {
                  const updated = editValues.alternative_answers.filter((_, i) => i !== idx);
                  setEditValues({ ...editValues, alternative_answers: updated });
                }}
                style={{
                  ...BUTTON_BASE,
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  color: '#dc2626',
                  border: '1.5px solid #dc2626',
                  fontSize: '11px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                  e.currentTarget.style.color = 'white';
                  handleButtonEnter(e);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#dc2626';
                  handleButtonLeave(e);
                }}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setEditValues({
              ...editValues,
              alternative_answers: [...(editValues.alternative_answers || []), '']
            })}
            style={{
              ...BUTTON_BASE,
              padding: '4px 10px',
              backgroundColor: '#16a34a',
              color: 'white',
              fontSize: '11px',
              marginTop: '4px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#15803d';
              handleButtonEnter(e);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#16a34a';
              handleButtonLeave(e);
            }}
          >
            <Plus size={12} /> Add Alternative
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleSave}
            style={{
              ...BUTTON_BASE,
              padding: '4px 12px',
              backgroundColor: '#16a34a',
              color: 'white',
              fontSize: '12px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#15803d';
              handleButtonEnter(e);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#16a34a';
              handleButtonLeave(e);
            }}
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={handleCancel}
            style={{
              ...BUTTON_BASE,
              padding: '4px 12px',
              backgroundColor: '#6b7280',
              color: 'white',
              fontSize: '12px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#4b5563';
              handleButtonEnter(e);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#6b7280';
              handleButtonLeave(e);
            }}
          >
            <X size={12} /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginBottom: '15px',
      paddingBottom: '10px',
      borderBottom: index < 100 ? '1px solid #e5e7eb' : 'none',
      borderRadius: '4px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: '4px' }}>
            <strong style={{ color: '#dc2626' }}>Q{qa.qa_order}:</strong>{' '}
            <span>{qa.question}</span>
          </div>
          <div>
            <strong style={{ color: '#16a34a' }}>A:</strong>{' '}
            <span>{qa.answer}</span>
            {qa.grammar_categories && qa.grammar_categories.length > 0 && (
              <span style={{ color: '#7c3aed', fontSize: '0.8rem', marginLeft: '8px', padding: '1px 6px', backgroundColor: '#ede9fe', borderRadius: '4px' }}>
                {Array.isArray(qa.grammar_categories) ? qa.grammar_categories.join(', ') : qa.grammar_categories}
              </span>
            )}
            {qa.alternative_answers && qa.alternative_answers.length > 0 && (
              <span style={{ color: '#6b7280', fontSize: '0.85rem', marginLeft: '8px' }}>
                (+{qa.alternative_answers.length} {qa.alternative_answers.length === 1 ? 'alternativa' : 'alternativ'})
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setIsEditing(true)}
          style={{
            ...BUTTON_BASE,
            padding: '4px 10px',
            backgroundColor: '#3b82f6',
            color: 'white',
            fontSize: '11px',
            marginLeft: '10px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2563eb';
            handleButtonEnter(e);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#3b82f6';
            handleButtonLeave(e);
          }}
        >
          <Pencil size={12} /> Edit
        </button>
      </div>
    </div>
  );
};

// Sub-component for dialogue line editing
const DialogueLineEditor = ({ line, index, onChange, onRemove }) => {
  return (
    <div style={{
      display: 'flex',
      gap: '10px',
      marginBottom: '10px',
      alignItems: 'center',
      padding: '10px',
      backgroundColor: '#f9fafb',
      borderRadius: '6px'
    }}>
      <span style={{ color: '#6b7280', fontWeight: 'bold', minWidth: '25px' }}>{index + 1}.</span>
      <input
        type="text"
        placeholder="Role"
        value={line.role || ''}
        onChange={(e) => onChange(index, { ...line, role: e.target.value })}
        style={{
          ...INPUT_STYLE,
          width: '120px',
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      <input
        type="text"
        placeholder="Text"
        value={line.text || ''}
        onChange={(e) => onChange(index, { ...line, text: e.target.value })}
        style={{
          ...INPUT_STYLE,
          flex: 1,
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      <select
        value={line.gender || ''}
        onChange={(e) => onChange(index, { ...line, gender: e.target.value || null })}
        style={{
          ...INPUT_STYLE,
          width: '100px',
          backgroundColor: line.gender === 'female' ? '#ffe6e6' : line.gender === 'male' ? '#e6f0ff' : 'white'
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <option value="">-- Hlas --</option>
        <option value="male">Muž</option>
        <option value="female">Žena</option>
      </select>
      <button
        onClick={() => onRemove(index)}
        style={{
          ...BUTTON_BASE,
          padding: '8px 12px',
          backgroundColor: 'transparent',
          color: '#dc2626',
          border: '1.5px solid #dc2626',
          fontSize: '12px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#dc2626';
          e.currentTarget.style.color = 'white';
          handleButtonEnter(e);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#dc2626';
          handleButtonLeave(e);
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
};

// Sub-component for structured dialogue editor
const DialogueEditor = ({ dialogue, onChange }) => {
  const handleLineChange = (index, newLine) => {
    const updated = [...dialogue];
    updated[index] = newLine;
    onChange(updated);
  };

  const handleRemoveLine = (index) => {
    const updated = dialogue.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleAddLine = () => {
    onChange([...dialogue, { role: '', text: '', gender: 'male' }]);
  };

  return (
    <div>
      {dialogue.map((line, index) => (
        <DialogueLineEditor
          key={index}
          line={line}
          index={index}
          onChange={handleLineChange}
          onRemove={handleRemoveLine}
        />
      ))}
      <button
        onClick={handleAddLine}
        style={{
          ...BUTTON_BASE,
          padding: '8px 16px',
          backgroundColor: '#16a34a',
          color: 'white',
          fontSize: '14px',
          marginTop: '10px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#15803d';
          handleButtonEnter(e);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#16a34a';
          handleButtonLeave(e);
        }}
      >
        <Plus size={14} /> Přidat řádek
      </button>
    </div>
  );
};

// Sub-component for structured Q&A pairs editor (used in Add New form)
const QAEditor = ({ qaPairs, onChange }) => {
  const handleChange = (index, field, value) => {
    const updated = [...qaPairs];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleAlternativeChange = (qaIndex, altIndex, value) => {
    const updated = [...qaPairs];
    const alts = [...(updated[qaIndex].alternative_answers || [])];
    alts[altIndex] = value;
    updated[qaIndex] = { ...updated[qaIndex], alternative_answers: alts };
    onChange(updated);
  };

  const handleAddAlternative = (qaIndex) => {
    const updated = [...qaPairs];
    updated[qaIndex] = {
      ...updated[qaIndex],
      alternative_answers: [...(updated[qaIndex].alternative_answers || []), '']
    };
    onChange(updated);
  };

  const handleRemoveAlternative = (qaIndex, altIndex) => {
    const updated = [...qaPairs];
    updated[qaIndex] = {
      ...updated[qaIndex],
      alternative_answers: updated[qaIndex].alternative_answers.filter((_, i) => i !== altIndex)
    };
    onChange(updated);
  };

  const handleAdd = () => {
    onChange([...qaPairs, { question: '', answer: '', alternative_answers: [], qa_order: qaPairs.length + 1, grammar_categories: [] }]);
  };

  const handleRemove = (index) => {
    onChange(qaPairs.filter((_, i) => i !== index));
  };

  return (
    <div>
      {qaPairs.map((qa, index) => (
        <div key={index} style={{
          marginBottom: '15px',
          padding: '10px',
          backgroundColor: '#f0fdf4',
          borderRadius: '6px',
          border: '1px solid #bbf7d0',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Pořadí:</label>
              <input
                type="number"
                value={qa.qa_order}
                onChange={(e) => handleChange(index, 'qa_order', parseInt(e.target.value) || 1)}
                style={{ ...INPUT_STYLE, width: '60px' }}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
            <button
              onClick={() => handleRemove(index)}
              style={{
                ...BUTTON_BASE,
                padding: '4px 8px',
                backgroundColor: 'transparent',
                color: '#dc2626',
                border: '1.5px solid #dc2626',
                fontSize: '11px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dc2626';
                e.currentTarget.style.color = 'white';
                handleButtonEnter(e);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#dc2626';
                handleButtonLeave(e);
              }}
            >
              <Trash2 size={12} /> Odebrat Q&A
            </button>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Otázka:</label>
            <textarea
              value={qa.question}
              onChange={(e) => handleChange(index, 'question', e.target.value)}
              placeholder="Zadejte otázku..."
              style={{ ...INPUT_STYLE, width: '100%', minHeight: '40px' }}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Odpověď:</label>
            <textarea
              value={qa.answer}
              onChange={(e) => handleChange(index, 'answer', e.target.value)}
              placeholder="Zadejte odpověď..."
              style={{ ...INPUT_STYLE, width: '100%', minHeight: '40px' }}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Grammar Categories:</label>
            <GrammarCategoriesField
              value={qa.grammar_categories}
              onChange={(val) => handleChange(index, 'grammar_categories', val)}
            />
          </div>
          <div style={{ marginBottom: '4px' }}>
            <label style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', display: 'block', marginBottom: '4px' }}>Alternativní odpovědi:</label>
            {(qa.alternative_answers || []).map((alt, altIdx) => (
              <div key={altIdx} style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                <input
                  type="text"
                  value={alt}
                  onChange={(e) => handleAlternativeChange(index, altIdx, e.target.value)}
                  style={{ ...INPUT_STYLE, flex: 1 }}
                  placeholder="Alternativní odpověď..."
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <button
                  onClick={() => handleRemoveAlternative(index, altIdx)}
                  style={{
                    ...BUTTON_BASE,
                    padding: '4px 8px',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    border: '1.5px solid #dc2626',
                    fontSize: '11px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                    e.currentTarget.style.color = 'white';
                    handleButtonEnter(e);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#dc2626';
                    handleButtonLeave(e);
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <button
              onClick={() => handleAddAlternative(index)}
              style={{
                ...BUTTON_BASE,
                padding: '4px 10px',
                backgroundColor: '#0891b2',
                color: 'white',
                fontSize: '11px',
                marginTop: '4px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#0e7490';
                handleButtonEnter(e);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#0891b2';
                handleButtonLeave(e);
              }}
            >
              <Plus size={12} /> Přidat alternativu
            </button>
          </div>
        </div>
      ))}
      <button
        onClick={handleAdd}
        style={{
          ...BUTTON_BASE,
          padding: '8px 16px',
          backgroundColor: '#16a34a',
          color: 'white',
          fontSize: '14px',
          marginTop: '5px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#15803d';
          handleButtonEnter(e);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#16a34a';
          handleButtonLeave(e);
        }}
      >
        <Plus size={14} /> Přidat Q&A pár
      </button>
    </div>
  );
};

export default ConversationManager;
